import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { useRef, useState, useContext } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ThemeContext } from "@/App";
import logoSecurefileMft from "@/assets/logo-securefile-mft-isotipo.webp";
import logoSecurefileAgent from "@/assets/logo-securefile-agent-isotipo.webp";
import logoWsGuardian from "@/assets/logo-wsguardian-isotipo.webp";
import logoAnkaSecure from "@/assets/logo-ankatech-isotipo-color.webp";
import logoAnkaSecureWhite from "@/assets/logo-ankatech-isotipo-white.webp";

const productData = [
  { key: "securefileMft", logo: logoSecurefileMft, logoDark: logoSecurefileMft, href: "/productos/securefile-mft", gradient: "from-[hsl(213,58%,20%)] to-[hsl(207,78%,40%)]", accent: "213,58%,20%" },
  { key: "securefileAgent", logo: logoSecurefileAgent, logoDark: logoSecurefileAgent, href: "/productos/securefile-agent", gradient: "from-[hsl(213,58%,20%)] to-[hsl(207,78%,40%)]", accent: "213,58%,20%" },
  { key: "wsGuardian", logo: logoWsGuardian, logoDark: logoWsGuardian, href: "/productos/ws-guardian", gradient: "from-[hsl(213,58%,20%)] to-[hsl(207,78%,40%)]", accent: "213,58%,20%" },
  { key: "ankaSecure", logo: logoAnkaSecure, logoDark: logoAnkaSecureWhite, href: "/productos/ankasecure", gradient: "from-[hsl(213,58%,20%)] to-[hsl(207,78%,40%)]", accent: "213,58%,20%" },
];

function ProductCard3D({ product, index }: { product: typeof productData[0]; index: number }) {
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setRotateX(-y * 15);
    setRotateY(x * 15);
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
    setIsHovered(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 80, rotateX: -15 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.8, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
      style={{ perspective: 1000 }}
    >
      <div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
        style={{
          transform: `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) ${isHovered ? "translateZ(20px)" : ""}`,
          transition: "transform 0.2s ease-out",
        }}
        className="group relative h-full"
      >
        {/* Animated glow behind card */}
        <motion.div
          className="absolute -inset-1 rounded-3xl opacity-0 blur-2xl"
          style={{ background: `radial-gradient(circle, hsla(${product.accent}, 0.4) 0%, transparent 70%)` }}
          animate={{ opacity: isHovered ? 0.3 : 0 }}
          transition={{ duration: 0.4 }}
        />

        <div className="relative bg-card rounded-2xl border border-border p-7 lg:p-8 h-full flex flex-col overflow-hidden group-hover:border-primary/30 transition-all duration-500">
          {/* Animated shine effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.07] to-transparent -skew-x-12"
            initial={{ x: "-200%" }}
            animate={isHovered ? { x: "200%" } : { x: "-200%" }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
          />

          {/* Corner accent */}
          <div
            className={`absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-bl ${product.gradient} opacity-[0.08] rounded-full group-hover:opacity-[0.15] group-hover:scale-150 transition-all duration-700`}
          />

          {/* Logo with bounce */}
          <motion.div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
            whileHover={{ scale: 1.15, rotate: 5 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <img loading="lazy" decoding="async" width={64} height={64}
              src={isDark ? product.logoDark : product.logo}
              alt={t(`productsSection.${product.key}.title`)}
              className="w-16 h-16 object-contain"
            />
          </motion.div>

          <h3 className="text-lg font-bold mb-3 text-foreground group-hover:text-primary transition-colors duration-300">
            {t(`productsSection.${product.key}.title`)}
          </h3>

          <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-grow">
            {t(`productsSection.${product.key}.description`)}
          </p>

          <Link to={product.href}>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button variant="outline" className="w-full group/btn border-border hover:border-primary/40 hover:bg-primary/5">
                {t("productsSection.viewMore")}
                <motion.span
                  className="inline-block"
                  initial={{ x: 0 }}
                  whileHover={{ x: 4 }}
                >
                  <ArrowRight className="w-4 h-4" />
                </motion.span>
              </Button>
            </motion.div>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export function ProductsSection() {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(titleRef, { once: true, margin: "-100px" });

  return (
    <section id="productos" className="relative py-14 lg:py-20 overflow-hidden">
      <div className="absolute inset-0 bg-card" />
      <div className="absolute inset-0 tech-pattern" />

      {/* Top divider with animated gradient */}
      <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
        <motion.div
          className="h-full w-[200%] bg-gradient-to-r from-transparent via-primary/40 to-transparent"
          animate={{ x: ["-50%", "0%"] }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative">
        <div ref={titleRef} className="max-w-4xl mx-auto text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <span className="text-sm text-primary font-medium">{t("productsSection.badge")}</span>
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-xl lg:text-3xl font-bold mb-5 text-foreground"
          >
            <span className="lg:whitespace-nowrap">
              {t("productsSection.title")}{" "}
              <span className="gradient-text">{t("productsSection.titleHighlight")}</span>{" "}
              {t("productsSection.titleEnd")}
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="text-lg text-muted-foreground"
          >
            {t("productsSection.subtitle")}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
          {productData.map((product, index) => (
            <ProductCard3D key={product.key} product={product} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
